import type { NuvoToastProps, ToastType } from '@nuvolo/nuux-core/components/NuvoToast';
import { ActionButton } from '@nuvolo/nuux-core/components/NuvoToast';
import type { PropsWithChildren } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import type { AskButton, ToastContextType } from './ToastContext';
import ToastContext from './ToastContext';
import StyledNuvoToast from './Styles';

const ToastProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [toast, setToast] = useState<NuvoToastProps>({ type: 'info' });

  const hideToast = useCallback(() => {
    setToast((t) => ({ ...t, visible: false }));
  }, []);

  const ask = useCallback(
    <R,>(type: ToastType, message: string, btns: AskButton<R>[]): Promise<R> => {
      return new Promise((res) => {
        setToast({
          visible: true,
          type,
          children: <div className="custom-toast-message">{message}</div>,
          autoClose: btns.length === 0,
          elementAttr: {
            class: `custom-toast custom-toast-${type}`,
          },
          actions: btns.map((b) => (
            <ActionButton
              className="nuvo-toast-action-button"
              key={b.key}
              label={b.label}
              onClick={() => {
                hideToast();
                res(b.value);
              }}
            />
          )),
        });
      });
    },
    [hideToast],
  );

  const confirm = useCallback(
    (
      type: ToastType,
      message: string,
      approveCancelTxt: [string, string] = ['Cancel', 'Approve'],
    ): Promise<boolean> => {
      return ask<boolean>(type, message, [
        { key: 'cancel', label: approveCancelTxt[0], value: false },
        { key: 'approve', label: approveCancelTxt[1], value: true },
      ]);
    },
    [ask],
  );

  const alert = useCallback(
    (type: ToastType, message: string): Promise<void> => {
      return ask<void>(type, message, []);
    },
    [ask],
  );

  const context = useMemo<ToastContextType>(
    () => ({
      ask,
      confirm,
      alert,
    }),
    [alert, ask, confirm],
  );

  return (
    <ToastContext.Provider value={context}>
      <StyledNuvoToast {...toast} hideOnOutsideClick onClose={hideToast} onHiding={hideToast} />
      {children}
    </ToastContext.Provider>
  );
};

export default React.memo(ToastProvider);
